import styled, { css } from 'styled-components';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { Price, PriceComponent } from '../../../common';
import {
  Description,
  DescriptionComponent,
  Icons,
  IconsComponent,
  Image,
  Name,
  NameComponent,
  StyledIconsItem
} from '../items-parts';
import type { SelectedItemLayout } from './ItemLayout';

export function ItemLayoutOne({
  data,
  width = 100,
  priceWidth,
  setPriceWidth,
  onClick,
  onKeyDown
}: SelectedItemLayout) {
  const { itemElements = [], showDefaultImage, popupEnabled } = useSettings();

  const { name, image, price, description, badges } = data;

  const showDots = price && itemElements.includes('price');

  const interactiveProps = popupEnabled
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown
      }
    : {};

  return (
    <Component
      {...interactiveProps}
      style={{
        width: `calc(${width}% - 48px)`
      }}
      _priceWidth={priceWidth}
    >
      {itemElements.includes('image') && (image || showDefaultImage) && (
        <Image image={image} alt="" />
      )}

      <Info>
        <NameAndPrice>
          <ItemLayoutOneNameContainer _showDots={!!showDots}>
            <Name>{name}</Name>

            {badges?.length > 0 && itemElements.includes('badges') && (
              <Icons icons={badges} />
            )}
          </ItemLayoutOneNameContainer>

          {price && itemElements.includes('price') && (
            <Price
              price={price}
              priceWidth={priceWidth}
              setPriceWidth={setPriceWidth}
            />
          )}
        </NameAndPrice>

        {description && itemElements.includes('description') && (
          <Description>{description}</Description>
        )}
      </Info>
    </Component>
  );
}

const Component = styled.div<{ _priceWidth: number }>`
  display: flex;
  align-items: flex-start;
  margin: 16px 24px;

  ${NameComponent} {
    display: inline;
    margin-right: 6px;
    text-align: left;
  }

  ${DescriptionComponent} {
    margin-right: ${({ _priceWidth }) => _priceWidth + 12}px;
  }

  &:hover,
  &:focus-visible {
    ${({ theme: { popupEnabled } }) =>
      popupEnabled &&
      css`
        cursor: pointer;

        ${NameComponent} {
          text-decoration: underline;
        }
      `}
  }

  ${focusVisibleMixin()}

  ${PriceComponent} {
    white-space: nowrap;
  }
`;
const Info = styled.div`
  width: 100%;
`;
const NameAndPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;
export const ItemLayoutOneNameContainer = styled.div<{ _showDots?: boolean }>`
  position: relative;
  width: 100%;
  margin-right: 12px;
  font-weight: bold;
  overflow: hidden;

  &::after {
    content: '';
    background-image: linear-gradient(
      to right,
      black 22%,
      rgba(255, 255, 255, 0) 0%
    );
    background-position: bottom;
    background-size: 6px 2px;
    background-repeat: repeat-x;
    margin: 0 4px;
    opacity: 0.5;
    display: ${({ _showDots }) => (_showDots ? 'inline-block' : 'none')};
    white-space: nowrap;
    overflow: hidden;
    position: absolute;
    width: 100%;
    height: ${({
      theme: {
        itemNameFontStyle: { fontSize }
      }
    }) => fontSize}px;
  }

  ${IconsComponent} {
    display: inline;
  }

  ${StyledIconsItem} {
    display: inline-block;
    width: ${({
      theme: {
        itemNameFontStyle: { fontSize }
      }
    }) => fontSize}px;
    height: ${({
      theme: {
        itemNameFontStyle: { fontSize }
      }
    }) => fontSize}px;
  }
`;
